import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Chip, Button,
  TextField, Divider, List, ListItem, ListItemText,
  Select, MenuItem, FormControl, InputLabel, Tab, Tabs,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel, Delete, PictureAsPdf, Download, History } from '@mui/icons-material';
import { ticketService } from '../services/ticketService';
import { commentService } from '../services/commentService';
import { userService } from '../services/userService';
import { kcService } from '../services/kcService';
import { categoryService } from '../services/categoryService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import AttachmentManager from '../components/AttachmentManager';
import { workReportService } from '../services/workReportService';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [kcs, setKcs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedTicket, setEditedTicket] = useState({});
  const [newComment, setNewComment] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [workReports, setWorkReports] = useState([]);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [newWorkReport, setNewWorkReport] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
    userId: ''
  });
  const [editingReport, setEditingReport] = useState(null);

  useEffect(() => {
    loadTicketData();
    loadUsers();
    loadKcs();
    loadCategories();
    loadLocations();
    loadWorkReports();
    loadHistory();
    
    // Prüfe URL-Parameter für Tab
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab) {
      setTabValue(parseInt(tab));
      
      // Scrolle nach kurzer Verzögerung zu den Tabs
      setTimeout(() => {
        const tabsElement = document.querySelector('[role="tablist"]');
        if (tabsElement) {
          tabsElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      }, 100);
    }
  }, [id, location.search]);

  const loadTicketData = async () => {
    try {
      const [ticketData, commentsData] = await Promise.all([
        ticketService.getTicketById(id),
        commentService.getCommentsByTicket(id)
      ]);
      setTicket(ticketData);
      setComments(commentsData);
      setEditedTicket(ticketData);
    } catch (error) {
      toast.error('Ticket konnte nicht geladen werden');
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const loadKcs = async () => {
    try {
      const kcsData = await kcService.getAllKcs();
      setKcs(kcsData);
    } catch (error) {
      console.error('Failed to load KCs');
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoryService.getAllCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadLocations = async () => {
    try {
      const locationsData = await kcService.getAllLocations();
      setLocations(locationsData);
    } catch (error) {
      console.error('Failed to load locations');
    }
  };

  const loadWorkReports = async () => {
    try {
      const reportsData = await workReportService.getWorkReportsByTicket(id);
      setWorkReports(reportsData);
    } catch (error) {
      console.error('Failed to load work reports');
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tickets/${id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTicketHistory(data);
      }
    } catch (error) {
      console.error('Failed to load history');
    }
  };

  // Status-Workflow für Techniker: Annehmen (in_progress) / Fertig melden (resolved)
  const handleWorkflow = async (newStatus) => {
    try {
      const result = await ticketService.updateTicket(id, { status: newStatus });
      if (result?._offline) {
        setTicket({ ...ticket, status: newStatus });
        toast.info('Offline – Änderung wird synchronisiert sobald wieder online');
      } else {
        setTicket(result);
        loadHistory();
        toast.success(newStatus === 'in_progress' ? 'Ticket angenommen' : 'Als fertig gemeldet');
      }
    } catch (error) {
      toast.error('Statusänderung fehlgeschlagen');
    }
  };

  const handleUpdate = async () => {
    try {
      const result = await ticketService.updateTicket(id, editedTicket);
      if (result?._offline) {
        // Offline queued – update local state optimistically
        setTicket({ ...ticket, ...editedTicket });
        setEditMode(false);
        toast.info('Offline – Änderung wird synchronisiert sobald wieder online');
      } else {
        setTicket(result);
        setEditMode(false);
        loadHistory();
        toast.success('Ticket erfolgreich aktualisiert');
      }
    } catch (error) {
      toast.error('Ticket konnte nicht aktualisiert werden');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const result = await commentService.createComment({
        ticket_id: id,
        content: newComment
      });
      if (result?.data?._offline) {
        setNewComment('');
        toast.info('Offline – Kommentar wird synchronisiert sobald wieder online');
      } else {
        setNewComment('');
        loadTicketData();
        toast.success('Kommentar hinzugefügt');
      }
    } catch (error) {
      toast.error('Kommentar konnte nicht hinzugefügt werden');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'warning',
      in_progress: 'info',
      resolved: 'success',
      closed: 'default',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Offen',
      in_progress: 'In Bearbeitung',
      resolved: 'Fertig Bearbeitet',
      closed: 'Abgerechnet',
      cancelled: 'Storniert'
    };
    return labels[status] || status;
  };


  if (!ticket) return <Typography>Laden...</Typography>;

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/tickets')}
        sx={{ mb: 2 }}
        size="small"
      >
        Zurück zu Tickets
      </Button>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">
            Ticket #{ticket.id}
          </Typography>
          <Box display="flex" gap={1}>
            {!editMode && (
              <>
                <Button
                  startIcon={<PictureAsPdf />}
                  variant="outlined"
                  color="error"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`/api/tickets/${id}/pdf`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Ticket_${ticket.external_ticket_number || ticket.id}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('PDF heruntergeladen');
                    } catch (error) {
                      toast.error('PDF-Export fehlgeschlagen');
                    }
                  }}
                >
                  PDF
                </Button>
                {isAdmin ? (
                  <Button startIcon={<Edit />} onClick={() => setEditMode(true)}>
                    Bearbeiten
                  </Button>
                ) : (
                  <>
                    {ticket.status === 'open' && (
                      <Button variant="contained" color="info" onClick={() => handleWorkflow('in_progress')}>
                        Annehmen
                      </Button>
                    )}
                    {ticket.status === 'in_progress' && (
                      <Button variant="contained" color="success" onClick={() => handleWorkflow('resolved')}>
                        Fertig melden
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        {!editMode ? (
          <>
            <Typography variant="h6" gutterBottom>
              {ticket.title}
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <Chip
                label={getStatusLabel(ticket.status)}
                color={getStatusColor(ticket.status)}
                size="small"
              />
              {ticket.category_name && (
                <Chip label={ticket.category_name} size="small" />
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Bayernwerk Ticket-Nr:</Typography>
                <Typography variant="body2">{ticket.external_ticket_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Seriennummer:</Typography>
                <Typography variant="body2">{ticket.serial_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Geräteklasse:</Typography>
                <Typography variant="body2">{ticket.device_class || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Gerätetyp:</Typography>
                <Typography variant="body2">{ticket.device_type || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Hersteller:</Typography>
                <Typography variant="body2">{ticket.manufacturer || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Technischer Platz:</Typography>
                <Typography variant="body2">{ticket.technical_place || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Planergruppe:</Typography>
                <Typography variant="body2">{ticket.planner_group || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Buchungskreis:</Typography>
                <Typography variant="body2">{ticket.booking_circle || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">KC:</Typography>
                <Typography variant="body2">{ticket.kc_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Standort:</Typography>
                <Typography variant="body2">{ticket.location || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">KVS/TH:</Typography>
                <Typography variant="body2">{ticket.kvs_th || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Ortsteil:</Typography>
                <Typography variant="body2">{ticket.district || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Sachbearbeiter:</Typography>
                <Typography variant="body2">{ticket.assigned_to_name || 'Nicht zugewiesen'}</Typography>
              </Box>
            </Box>
            
            {/* Zeige Karte wenn vorhanden */}
            {ticket.map_image_path && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Ortskarte:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  overflow: 'hidden'
                }}>
                  {ticket.map_image_path.toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      src={`${ticket.map_image_path}`}
                      title={`Karte für ${ticket.district || ticket.location}`}
                      style={{ 
                        width: '100%', 
                        height: '600px', 
                        border: 'none',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <img 
                      src={`${ticket.map_image_path}`} 
                      alt={`Karte für ${ticket.district || ticket.location}`}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  )}
                </Box>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(`${ticket.map_image_path}`, '_blank')}
                  >
                    In neuem Tab öffnen
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    href={`${ticket.map_image_path}`}
                    download
                  >
                    Herunterladen
                  </Button>
                </Box>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mt: 2 }}>
              Beschreibung:
            </Typography>
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="textSecondary">
              Erstellt von: {ticket.created_by_name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Zugewiesen an: {ticket.assigned_to_name || 'Unassigned'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Erstellt am: {format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}
            </Typography>
            {ticket.report_date && (
              <Typography variant="body2" color="textSecondary">
                Meldedatum: {format(new Date(ticket.report_date), 'dd.MM.yyyy HH:mm')}
              </Typography>
            )}
          </>
        ) : (
          <>
            <TextField
              fullWidth
              label="Titel"
              value={editedTicket.title}
              onChange={(e) => setEditedTicket({ ...editedTicket, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Beschreibung"
              value={editedTicket.description}
              onChange={(e) => setEditedTicket({ ...editedTicket, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editedTicket.status}
                  label="Status"
                  onChange={(e) => setEditedTicket({ ...editedTicket, status: e.target.value })}
                >
                  <MenuItem value="open">Offen</MenuItem>
                  <MenuItem value="in_progress">In Bearbeitung</MenuItem>
                  <MenuItem value="resolved">Fertig Bearbeitet</MenuItem>
                  <MenuItem value="closed">Abgerechnet</MenuItem>
                  <MenuItem value="cancelled">Storniert</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={editedTicket.category_id || ''}
                  label="Kategorie"
                  onChange={(e) => setEditedTicket({ ...editedTicket, category_id: e.target.value })}
                >
                  <MenuItem value="">Keine Kategorie</MenuItem>
                  {categories.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {user?.role === 'admin' && (
                <FormControl fullWidth>
                  <InputLabel>Zugewiesen an</InputLabel>
                  <Select
                    value={editedTicket.assigned_to || ''}
                    label="Zugewiesen an"
                    onChange={(e) => setEditedTicket({ ...editedTicket, assigned_to: e.target.value })}
                  >
                    <MenuItem value="">Nicht zugewiesen</MenuItem>
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>Zusätzliche Informationen</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField
                label="Bayernwerk Ticket-Nr"
                value={editedTicket.external_ticket_number || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, external_ticket_number: e.target.value })}
              />
              <TextField
                label="Seriennummer"
                value={editedTicket.serial_number || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, serial_number: e.target.value })}
              />
              <TextField
                label="Geräteklasse"
                value={editedTicket.device_class || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, device_class: e.target.value })}
              />
              <TextField
                label="Gerätetyp"
                value={editedTicket.device_type || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, device_type: e.target.value })}
              />
              <TextField
                label="Hersteller"
                value={editedTicket.manufacturer || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, manufacturer: e.target.value })}
              />
              <TextField
                label="Technischer Platz"
                value={editedTicket.technical_place || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, technical_place: e.target.value })}
              />
              <TextField
                label="Planergruppe"
                value={editedTicket.planner_group || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, planner_group: e.target.value })}
              />
              <TextField
                label="Buchungskreis"
                value={editedTicket.booking_circle || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, booking_circle: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>KC</InputLabel>
                <Select
                  value={editedTicket.competence_center_id || ''}
                  label="KC"
                  onChange={(e) => setEditedTicket({ ...editedTicket, competence_center_id: e.target.value })}
                >
                  <MenuItem value="">Kein KC</MenuItem>
                  {kcs.map(kc => (
                    <MenuItem key={kc.id} value={kc.id}>{kc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Standort"
                value={editedTicket.location || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, location: e.target.value })}
              />
              <TextField
                label="KVS/TH"
                value={editedTicket.kvs_th || ''}
                onChange={(e) => setEditedTicket({ ...editedTicket, kvs_th: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Ortsteil</InputLabel>
                <Select
                  value={editedTicket.location_id || ''}
                  label="Ortsteil"
                  onChange={(e) => {
                    const selectedLocation = locations.find(l => l.id === e.target.value);
                    setEditedTicket({ 
                      ...editedTicket, 
                      location_id: e.target.value,
                      district: selectedLocation ? selectedLocation.district : '',
                      location: selectedLocation ? selectedLocation.city : editedTicket.location
                    });
                  }}
                >
                  <MenuItem value="">Kein Ortsteil</MenuItem>
                  {locations
                    .filter(loc => !editedTicket.competence_center_id || loc.competence_center_id === editedTicket.competence_center_id)
                    .map(loc => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.plz ? `${loc.plz} ` : ''}{loc.city}{loc.district ? ` - ${loc.district}` : ''}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" gap={1}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleUpdate}
              >
                Speichern
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => setEditMode(false)}
              >
                Abbrechen
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`Zusatz (${comments.length})`} />
          <Tab label={`Anhänge${ticket.attachment_count > 0 ? ` (${ticket.attachment_count})` : ''}`} />
          <Tab label="Arbeitsbericht" />
          <Tab label={`Historie (${ticketHistory.length})`} />
        </Tabs>
        
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <List>
              {comments.map((comment) => (
            <ListItem key={comment.id} alignItems="flex-start">
              <ListItemText
                primary={comment.username}
                secondary={
                  <>
                    <Typography component="span" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment.content}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {format(new Date(comment.created_at), 'PPpp')}
                    </Typography>
                  </>
                }
              />
            </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />
            
            <Box mt={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Zusatz hinzufügen..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleAddComment}
                sx={{ mt: 1 }}
                disabled={!newComment.trim()}
              >
                Zusatz hinzufügen
              </Button>
            </Box>
          </Box>
        )}
        
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <AttachmentManager 
              ticketId={id} 
              canEdit={user?.role === 'admin' || user?.role === 'technician'}
            />
          </Box>
        )}
        
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Arbeitsberichte</Typography>
              {workReports.length > 0 && (
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<PictureAsPdf />}
                    onClick={async () => {
                      try {
                        await workReportService.exportPDF(id);
                        toast.success('Gesamt-PDF wurde heruntergeladen');
                      } catch (error) {
                        toast.error('Fehler beim PDF-Export');
                      }
                    }}
                    sx={{
                      backgroundColor: '#d32f2f',
                      '&:hover': { backgroundColor: '#b71c1c' }
                    }}
                  >
                    Gesamt-PDF exportieren
                  </Button>
                </Box>
              )}
            </Box>
            
{(() => {
              // Gruppiere Arbeitsberichte nach Datum
              const reportsByDate = workReports.reduce((acc, report) => {
                const dateKey = report.date;
                if (!acc[dateKey]) {
                  acc[dateKey] = [];
                }
                acc[dateKey].push(report);
                return acc;
              }, {});

              // Sortiere Daten absteigend
              const sortedDates = Object.keys(reportsByDate).sort((a, b) => new Date(b) - new Date(a));

              return (
                <>
                  {sortedDates.map((date) => {
                    const reportsForDate = reportsByDate[date];
                    const totalHoursForDate = reportsForDate.reduce((sum, r) => sum + parseFloat(r.hours), 0);

                    return (
                      <Box key={date} sx={{ mb: 3 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            mb: 1
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {format(new Date(date), 'dd.MM.yyyy')} - {totalHoursForDate.toFixed(2)} Stunden
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={async () => {
                              try {
                                // Konvertiere Datum zu YYYY-MM-DD Format
                                const dateStr = new Date(date).toISOString().split('T')[0];
                                await workReportService.exportDailyPDF(id, dateStr);
                                toast.success(`PDF für ${format(new Date(date), 'dd.MM.yyyy')} wurde heruntergeladen`);
                              } catch (error) {
                                toast.error('Fehler beim PDF-Export');
                              }
                            }}
                            sx={{
                              borderColor: '#d32f2f',
                              color: '#d32f2f',
                              '&:hover': {
                                borderColor: '#b71c1c',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)'
                              }
                            }}
                          >
                            PDF
                          </Button>
                        </Box>

                        <List>
                          {reportsForDate.map((report, index) => (
                            <ListItem
                              key={report.id || index}
                              alignItems="flex-start"
                              divider
                              secondaryAction={
                                user?.role === 'admin' && (
                                  <Box>
                                    <IconButton
                                      edge="end"
                                      aria-label="edit"
                                      sx={{ mr: 0.5 }}
                                      onClick={() => setEditingReport({
                                        id: report.id,
                                        date: report.date ? report.date.split('T')[0] : new Date().toISOString().split('T')[0],
                                        hours: report.hours,
                                        description: report.description,
                                        userId: report.user_id || ''
                                      })}
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={async () => {
                                        if (window.confirm('Arbeitsbericht wirklich löschen?')) {
                                          try {
                                            await workReportService.deleteWorkReport(report.id);
                                            setWorkReports(workReports.filter(r => r.id !== report.id));
                                            toast.success('Arbeitsbericht gelöscht');
                                          } catch (error) {
                                            toast.error('Fehler beim Löschen des Arbeitsberichts');
                                          }
                                        }
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Box>
                                )
                              }
                            >
                              <ListItemText
                                primary={`${report.hours} Stunden`}
                                secondary={
                                  <>
                                    <Typography component="span" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {report.description}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                      Erstellt von: {report.username || user?.username}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    );
                  })}
                </>
              );
            })()}
            
            {workReports.length === 0 && (
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                Noch keine Arbeitsberichte vorhanden
              </Typography>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>Neuen Arbeitsbericht hinzufügen</Typography>
            
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
              <TextField
                type="date"
                label="Datum"
                value={newWorkReport.date}
                onChange={(e) => setNewWorkReport({ ...newWorkReport, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', sm: 150 } }}
              />
              <TextField
                type="number"
                label="Stunden"
                value={newWorkReport.hours}
                onChange={(e) => setNewWorkReport({ ...newWorkReport, hours: e.target.value })}
                inputProps={{ step: 0.5, min: 0 }}
                sx={{ maxWidth: 100 }}
              />
              {user?.role === 'admin' && (
                <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                  <InputLabel shrink>Techniker</InputLabel>
                  <Select
                    label="Techniker"
                    displayEmpty
                    notched
                    value={newWorkReport.userId}
                    onChange={(e) => setNewWorkReport({ ...newWorkReport, userId: e.target.value })}
                    renderValue={(val) => {
                      if (!val) return 'Ich selbst';
                      const u = users.find(x => String(x.id) === String(val));
                      return u ? u.username : 'Ich selbst';
                    }}
                  >
                    <MenuItem value="">Ich selbst</MenuItem>
                    {users.filter(u => u.role === 'technician' || u.role === 'admin').map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Beschreibung der Arbeiten"
              placeholder="Was wurde gemacht..."
              value={newWorkReport.description}
              onChange={(e) => setNewWorkReport({ ...newWorkReport, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <Button
              variant="contained"
              onClick={async () => {
                if (newWorkReport.date && newWorkReport.hours && newWorkReport.description) {
                  try {
                    const createdReport = await workReportService.createWorkReport(id, newWorkReport);
                    setWorkReports([...workReports, createdReport]);
                    setNewWorkReport({
                      date: new Date().toISOString().split('T')[0],
                      hours: '',
                      description: '',
                      userId: ''
                    });
                    toast.success('Arbeitsbericht hinzugefügt');
                  } catch (error) {
                    toast.error('Fehler beim Speichern des Arbeitsberichts');
                  }
                } else {
                  toast.error('Bitte alle Felder ausfüllen');
                }
              }}
              disabled={!newWorkReport.date || !newWorkReport.hours || !newWorkReport.description}
            >
              Arbeitsbericht hinzufügen
            </Button>

            <Dialog open={!!editingReport} onClose={() => setEditingReport(null)} fullWidth maxWidth="sm">
              <DialogTitle>Arbeitsbericht bearbeiten</DialogTitle>
              <DialogContent>
                {editingReport && (
                  <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                      <TextField
                        type="date"
                        label="Datum"
                        value={editingReport.date}
                        onChange={(e) => setEditingReport({ ...editingReport, date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: { xs: '100%', sm: 150 } }}
                      />
                      <TextField
                        type="number"
                        label="Stunden"
                        value={editingReport.hours}
                        onChange={(e) => setEditingReport({ ...editingReport, hours: e.target.value })}
                        inputProps={{ step: 0.5, min: 0 }}
                        sx={{ maxWidth: 120 }}
                      />
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel>Techniker</InputLabel>
                      <Select
                        label="Techniker"
                        value={editingReport.userId || ''}
                        onChange={(e) => setEditingReport({ ...editingReport, userId: e.target.value })}
                      >
                        {users.filter(u => u.role === 'technician' || u.role === 'admin').map(u => (
                          <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Beschreibung der Arbeiten"
                      value={editingReport.description}
                      onChange={(e) => setEditingReport({ ...editingReport, description: e.target.value })}
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditingReport(null)}>Abbrechen</Button>
                <Button
                  variant="contained"
                  disabled={!editingReport?.date || !editingReport?.hours || !editingReport?.description}
                  onClick={async () => {
                    try {
                      const updated = await workReportService.updateWorkReport(editingReport.id, editingReport);
                      setWorkReports(workReports.map(r => r.id === editingReport.id ? updated : r));
                      setEditingReport(null);
                      toast.success('Arbeitsbericht aktualisiert');
                    } catch (error) {
                      toast.error('Fehler beim Aktualisieren des Arbeitsberichts');
                    }
                  }}
                >
                  Speichern
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {tabValue === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <History sx={{ mr: 1, verticalAlign: 'middle' }} />
              Änderungsverlauf
            </Typography>
            {ticketHistory.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                Noch keine Änderungen protokolliert.
              </Typography>
            ) : (
              <List>
                {ticketHistory.map((entry) => {
                  const actionLabels = {
                    status: 'Status',
                    priority: 'Priorität',
                    assigned_to: 'Zuweisung',
                    category_id: 'Kategorie',
                    title: 'Titel',
                    competence_center_id: 'Kompetenz-Center'
                  };
                  const actionLabel = actionLabels[entry.action] || entry.action;
                  return (
                    <ListItem key={entry.id} sx={{ borderBottom: '1px solid #eee', py: 1.5 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip label={actionLabel} size="small" color="primary" variant="outlined" />
                            <Typography variant="body2">
                              {entry.old_value ? (
                                <>
                                  <span style={{ textDecoration: 'line-through', color: '#999' }}>{entry.old_value}</span>
                                  {' → '}
                                  <strong>{entry.new_value}</strong>
                                </>
                              ) : (
                                <strong>{entry.new_value}</strong>
                              )}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {entry.username || 'System'} — {new Date(entry.created_at).toLocaleString('de-DE')}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TicketDetail;